import { SHIPPER_MUTATION_TYPES } from "../../mutation-types";
import {
  dangerousGoodDefault,
  certificateDocumentDefault,
  getDefaultState,
  getDefaultShippingRequestObject
} from "./index";

/**
 * Shipper mutations
 */

export default {
  /**
   * Update shipper and consignee details
   *
   * @param {*} state
   * @param { key, value} data
   */
  [SHIPPER_MUTATION_TYPES.UPDATE_SHIPPER_AND_CONSIGNEE_DETAILS](state, data) {
    state.shippingRequest.shipper_and_consignee_details = {
      ...state.shippingRequest.shipper_and_consignee_details,
      [data.key]: data.value
    };
  },

  /**
   * Update Additional Info Details
   *
   * @param {*} state
   * @param {key, value} data
   */
  [SHIPPER_MUTATION_TYPES.UPDATE_ADDITIONAL_INFO_AND_DOCS](state, data) {
    state.shippingRequest.additional_info_and_docs = {
      ...state.shippingRequest.additional_info_and_docs,
      [data.key]: data.value
    };
  },

  /**
   * Update dangerous goods
   *
   * @param {*} state
   * @param {*} data
   */
  [SHIPPER_MUTATION_TYPES.UPDATE_SHIPPER_DANGEROUS_GOODS_DETAILS](state, data) {
    let { index, keyName, value } = data;

    /**
     * We use the isMultiLevel check to update state objects 2 levels deep
     */
    const isMultiLevel = keyName.includes(".");
    const keys = keyName.split(".");

    if (isMultiLevel) {
      state.shippingRequest.dangerous_goods[index] = {
        ...state.shippingRequest.dangerous_goods[index]
      };
      state.shippingRequest.dangerous_goods[index][keys[0]][keys[1]] = value;
    } else {
      state.shippingRequest.dangerous_goods[index] = {
        ...state.shippingRequest.dangerous_goods[index],
        [keyName]: value
      };
    }
  },

  /**
   * Update certificate documents
   *
   * @param {*} state
   * @param {*} data
   */
  [SHIPPER_MUTATION_TYPES.UPDATE_CERTIFICATE_DOCUMENTS](state, data) {
    let { index, keyName, value } = data;

    /**
     * We use the isMultiLevel check to update state objects 2 levels deep
     */
    const isMultiLevel = keyName.includes(".");
    const keys = keyName.split(".");

    if (isMultiLevel) {
      state.shippingRequest.certificate_documents[index] = {
        ...state.shippingRequest.certificate_documents[index]
      };
      state.shippingRequest.certificate_documents[index][keys[0]][
        keys[1]
      ] = value;
    } else {
      state.shippingRequest.certificate_documents[index] = {
        ...state.shippingRequest.certificate_documents[index],
        [keyName]: value
      };
    }
  },

  /**
   * Save dangerous goods info to state
   *
   * @param {*} state
   * @param {*} dangerousGoodsInfo
   */
  [SHIPPER_MUTATION_TYPES.SAVE_DANGEROUS_GOODS_INFO](
    state,
    dangerousGoodsInfo
  ) {
    state.dangerousGoodsInfo = dangerousGoodsInfo;
  },

  /**
   * Add a new dangerous good
   *
   * @param {*} state
   */
  [SHIPPER_MUTATION_TYPES.ADD_NEW_DANGEROUS_GOOD](state) {
    state.shippingRequest.dangerous_goods = [
      ...state.shippingRequest.dangerous_goods,
      {
        ...dangerousGoodDefault
      }
    ];
  },

  /**
   * Delete a dangerous good
   * @param {*} state
   * @param {*} dangerousGoodIndex
   */
  [SHIPPER_MUTATION_TYPES.DELETE_DANGEROUS_GOOD](state, dangerousGoodIndex) {
    if (state.shippingRequest.dangerous_goods[dangerousGoodIndex]) {
      state.shippingRequest.dangerous_goods = state.shippingRequest.dangerous_goods.splice(
        dangerousGoodIndex,
        1
      );
    }
  },

  [SHIPPER_MUTATION_TYPES.ADD_NEW_CERTIFICATE_DOCUMENT](state) {
    state.shippingRequest.certificate_documents = [
      ...state.shippingRequest.certificate_documents,
      {
        ...certificateDocumentDefault
      }
    ];
  },

  [SHIPPER_MUTATION_TYPES.UPDATE_FORM_DETAILS](
    state,
    { id, reference_number }
  ) {
    state.shippingRequest.form_details = {
      id: id,
      reference_number: reference_number
    };
  },

  /**
   * Add a newly uploaded document to the store
   *
   * @param {*} state
   * @param {Object} documentDetails Details about the uploaded document
   */
  [SHIPPER_MUTATION_TYPES.ADD_UPLOADED_CERTIFICATE_DOCUMENT](
    state,
    { id, file_name, file_url }
  ) {
    state.shippingRequest.uploaded_certificate_documents = [
      ...state.shippingRequest.uploaded_certificate_documents,
      {
        id,
        file_name,
        file_url
      }
    ];
  },

  /**
   * Reset the certifcate document
   *
   * @param {*} state
   */
  [SHIPPER_MUTATION_TYPES.RESET_CERTIFICATE_DOCUMENTS](state) {
    state.shippingRequest.certificate_documents = [
      {
        ...certificateDocumentDefault
      }
    ];
  },

  /**
   * Delete an uploaded document
   *
   * @param {*} state
   * @param {*} document_id
   */
  [SHIPPER_MUTATION_TYPES.DELETE_UPLOADED_CERTIFICATE_DOCUMENT](
    state,
    document_id
  ) {
    state.shippingRequest.uploaded_certificate_documents = state.shippingRequest.uploaded_certificate_documents.filter(
      document => document.id !== document_id
    );
  },

  /**
   * Update the shipper declaration object
   *
   * @param {*} state
   * @param {*} param1
   */
  [SHIPPER_MUTATION_TYPES.UPDATE_SHIPPER_DECLARATION](state, { key, value }) {
    state.shippingRequest.shipper_declaration = {
      ...state.shippingRequest.shipper_declaration,
      [key]: value
    };
  },

  /**
   * Update the shipping request object with data gotten from the api
   *
   * @param {*} state
   * @param {*} param1
   */
  [SHIPPER_MUTATION_TYPES.UPDATE_SHIPPING_REQUEST_WITH_DATA_FROM_API](
    state,
    { data }
  ) {
    const dataFromServer = JSON.parse(data);
    state.shippingRequest = {
      ...state.shippingRequest,
      ...dataFromServer
    };
  },

  /**
   * Reset the shippers state
   *
   * @param {*} state
   */
  [SHIPPER_MUTATION_TYPES.RESET_STATE](state) {
    Object.assign(state, getDefaultState());
  },

  /**
   * Reset the shipping request object
   * @param {*} state
   */
  [SHIPPER_MUTATION_TYPES.RESET_SHIPPING_REQUEST](state) {
    state.shippingRequest = {
      ...getDefaultShippingRequestObject()
    };
  },

  /**
   * Save the shipping request statistics (no approved, pending e.t.c) to the store
   *
   * @param {*} state
   * @param {*} statistics
   */
  [SHIPPER_MUTATION_TYPES.SAVE_SHIPPING_REQUEST_STATISTICS](state, statistics) {
    state.shippingRequestStatistics = {
      ...state.shippingRequestStatistics,
      ...statistics
    };
  },

  /**
   * Store the shipper's saved shipping requests
   *
   * @param {*} state
   * @param {*} savedRequests
   */
  [SHIPPER_MUTATION_TYPES.SAVE_SAVED_SHIPPING_REQUESTS](state, savedRequests) {
    state.savedShippingRequests = savedRequests;
  },

  /**
   * Set a saved shipping request as the active shipping request object, so it can be continued.
   *
   * @param {*} state
   * @param {*} savedRequest
   */
  [SHIPPER_MUTATION_TYPES.CONTINUE_SAVED_SHIPPING_REQUEST](
    state,
    savedRequest
  ) {
    state.shippingRequest = {
      ...savedRequest,
      form_details: {
        ...savedRequest.form_details
      },
      shipper_and_consignee_details: {
        ...savedRequest.shipper_and_consignee_details
      },
      dangerous_goods: savedRequest.dangerous_goods,
      container_details: {
        ...savedRequest.container_details
      },
      certificate_documents: savedRequest.certificate_documents,
      uploaded_certificate_documents:
        savedRequest.uploaded_certificate_documents,
      shipper_declaration: savedRequest.shipper_declaration
    };
  },

  /**
   * Save all the shipping requests for a shipper
   *
   * @param {*} state
   * @param {*} shippingRequests
   */

  [SHIPPER_MUTATION_TYPES.SAVE_ALL_SHIPPER_SHIPPING_REQUESTS](
    state,
    shippingRequests
  ) {
    state.shippingRequests = shippingRequests;
  },

  /**
   * Save the search results for a shipper
   *
   * @param {*} state
   * @param {*} shippingRequests
   */

  [SHIPPER_MUTATION_TYPES.SAVE_SHIPPER_SEARCH_AND_FILTER_SHIPPING_REQUESTS](
    state,
    shippingRequests
  ) {
    state.searchAndFilterShippingRequests = shippingRequests;
  },

  /**
   * Save the approved shipping requests for a shipper
   *
   * @param {*} state
   * @param {*} shippingRequests
   */

  [SHIPPER_MUTATION_TYPES.SAVE_APPROVED_SHIPPING_REQUESTS](
    state,
    shippingRequests
  ) {
    state.approvedShippingRequests = shippingRequests;
  }
};
